import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import routes from "../../routes";
import NotFound from "../../Views/NotFound/NotFound";
import Sidebar from "../Sidebar";
import { MainNav } from "../MuiTable";
import "./style.css";
import "./admin-dark-overrides.css";
import { Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  AdminDarkModeProvider,
  useAdminDarkMode,
} from "../../contexts/AdminDarkModeContext";
import { updateCompanyNameFromToken, setPageTitle, getPageTitleFromRoute } from "../../utils/pageTitle";

const MOBILE_MQ = "(max-width: 767px)";

function subscribeAdminMobileLayout(callback) {
  if (typeof window === "undefined") return () => {};
  const mq = window.matchMedia(MOBILE_MQ);
  mq.addEventListener("change", callback);
  return () => mq.removeEventListener("change", callback);
}

function getAdminMobileLayoutSnapshot() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MOBILE_MQ).matches;
}

/** Assume narrow viewport on SSR so HTML is not emitted with desktop sidebar offset. */
function getAdminMobileLayoutServerSnapshot() {
  return true;
}

function AdminInner() {
  const { darkMode } = useAdminDarkMode();
  const mainContent = useRef(null);
  const { CompanyUrl } = useParams();
  const location = useLocation();
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const [isSidebarDisplay, setIsSidebarDisplay] = useState(true);
  /* useMediaQuery can report “desktop” on the first client paint → inline
     marginLeft: 260px briefly (or stuck), which matches the empty-left / shifted
     content bug on phones. useSyncExternalStore reads matchMedia synchronously. */
  const isMobileLayout = useSyncExternalStore(
    subscribeAdminMobileLayout,
    getAdminMobileLayoutSnapshot,
    getAdminMobileLayoutServerSnapshot
  );

  // Reset sidebar state when screen size changes
  useEffect(() => {
    if (!isMobileLayout) {
      setIsSidebarDisplay(true);
      setIsSidebarClosed(false);
    } else {
      setIsSidebarDisplay(false);
    }
  }, [isMobileLayout]);

  useEffect(() => {
    // JOB-3245: updateCompanyNameFromToken is handled by ProtectedRoute; don't
    // repeat it here. Just set the title synchronously.
    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("admin-dark-active");
    } else {
      document.body.classList.remove("admin-dark-active");
    }
    return () => document.body.classList.remove("admin-dark-active");
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }

    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, [location]);

  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.layout === `/:CompanyUrl` || prop.layout === `/${CompanyUrl}`) {
        if (prop?.children) {
          return prop?.children?.map((item, index) => (
            <Route
              path={`${item?.path}`}
              element={item?.component}
              key={index}
            />
          ));
        } else {
          return <Route path={prop?.path} element={prop?.component} key={key} />;
        }
      }
      return null;
    });
  };

  const adminTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          secondary: {
            main: darkMode ? "#f48fb1" : "#dc004e",
          },
          background: {
            default: darkMode ? "#0f1419" : "#f7f8fa",
            paper: darkMode ? "#1a2332" : "#ffffff",
          },
          ...(darkMode
            ? {
                text: {
                  primary: "#e8eaed",
                  secondary: "#9aa0a6",
                },
                divider: "#2d3b4e",
              }
            : {}),
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
        },
        typography: {
          fontFamily: "'Poppins', sans-serif",
          h1: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
          },
          h2: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 500,
          },
          body1: {
            fontFamily: "'Poppins', sans-serif",
          },
        },
      }),
    [darkMode]
  );

  return (
    <ThemeProvider theme={adminTheme}>
    <Grid
      className={`admin-shell${darkMode ? " admin-shell--dark" : ""}`}
      style={{
        backgroundColor: "var(--app-page-background)",
        width: "100vw",
        height: "100vh",
        overflow: "auto",
        overflowX: "hidden",
      }}
    >
      <Sidebar
        layout={`/:CompanyUrl`}
        isSidebarClosed={isSidebarClosed}
        setIsSidebarClosed={setIsSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
      />
      <MainNav
        layout="/:CompanyUrl"
        isSidebarClosed={isSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
      />
      <Grid
        className="main-content invoicePaidSlip"
        style={
          isMobileLayout
            ? {
                marginLeft: 0,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                paddingLeft: 15,
                paddingRight: 15,
                boxSizing: "border-box",
                transition: "all 0.3s ease",
              }
            : {
                marginLeft: isSidebarClosed ? "100px" : "260px",
                width: "auto",
                paddingLeft: 0,
                paddingRight: 0,
                transition: "all 0.3s ease",
              }
        }
        ref={mainContent}
      >
        <Grid
          style={{
            width: isMobileLayout ? "100%" : "98%",
            marginLeft: "auto",
            marginRight: "auto",
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
          className="customerDetail_paid"
        >
          <React.Suspense fallback={null}>
            <Routes>
              {getRoutes(routes)}
              {/* JOB-3225: singular/plural aliases so common typos reach the
                  canonical URL instead of a silent redirect to /index. */}
              <Route path="/quote" element={<Navigate to={`/${CompanyUrl}/quotes`} replace />} />
              <Route path="/contracts" element={<Navigate to={`/${CompanyUrl}/contract`} replace />} />
              <Route path="/invoices" element={<Navigate to={`/${CompanyUrl}/invoice`} replace />} />
              <Route path="/materials" element={<Navigate to={`/${CompanyUrl}/materials-labor`} replace />} />
              {/* JOB-3220 + JOB-3225: real 404 for anything that doesn't match.
                  No more silent /index redirect that leaves the typed URL in the
                  address bar with the dashboard content. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </React.Suspense>
        </Grid>
      </Grid>
    </Grid>
    </ThemeProvider>
  );
}

const Admin = () => (
  <AdminDarkModeProvider>
    <AdminInner />
  </AdminDarkModeProvider>
);

export default Admin;
